import { Button } from '@mui/material';
import { useForm, useFieldArray } from 'react-hook-form';
import { useRef } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useDeleteProcedureMutation, useProceduresMutation } from '../queries';
import AddStockAllowanceForm from './AddStockAllowanceForm';
import {
  AddProcedureButtonProps,
  getProcedureInvalidation,
  mapDefaultStockAlowances,
  PostNewProcedure,
  StockAllowanceFormValues,
} from '../entity';
import { useEditableAndReadonlyStockAllowances } from '../store';

import { SubmitFormDialog } from '@shared/components/dialogs/submit-form-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import ProcedureCard from './ProcedureCard';
import { queryClient } from '@shared/queries';
import { useSuspenseStocksQuery } from '../../queries';
import TextField from '@shared/components/TextField';

const ProcedureFormDialog = ({
  defaultValues,
  procedureId,
  hasNoProcedures,
  isVisitClosed,
  procedures,
  dialogRef: externalDialogRef,
}: AddProcedureButtonProps) => {
  const { t } = useIntlTranslations();
  const internalDialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const addProcedureDialogRef = externalDialogRef ?? internalDialogRef;
  const { editableAllowances, readonlyAllowances } = useEditableAndReadonlyStockAllowances(
    defaultValues?.stockAllowances,
  );

  const { visitId, clientId } = useParams();
  const { data: stocks } = useSuspenseStocksQuery();

  const methods = useForm<StockAllowanceFormValues>({
    defaultValues: {
      description: defaultValues?.description ?? '',
      stockAllowances: mapDefaultStockAlowances(editableAllowances),
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: 'stockAllowances',
    control: methods.control,
  });

  const { mutation: deleteProcedure } = useDeleteProcedureMutation({
    onSuccess: () => {
      getProcedureInvalidation(stocks, visitId, clientId);
    },
  });

  const { mutation: createNewProcedure } = useProceduresMutation({
    onSuccess: () => {
      getProcedureInvalidation(stocks, visitId, clientId);
      methods.reset();
      addProcedureDialogRef.current?.close();
      queryClient.invalidateQueries({ queryKey: ['visits'] });
    },
  });

  // const handleClickOpen = () => {
  //   if (defaultValues) {
  //     const stockAllowancesDefault = mapDefaultStockAlowances(editableAllowances);
  //     replace(stockAllowancesDefault);
  //   }

  //   setOpen(true);
  // };

  // const handleClose = () => {
  //   setOpen(false);
  // };

  return (
    <>
      {hasNoProcedures && (
        <Button
          onClick={() => addProcedureDialogRef.current?.open()}
          variant="outlined"
          disabled={isVisitClosed || createNewProcedure.isPending}
          sx={{ boxShadow: '0px 0px 6px 2px rgba(0,0,0,0.15)' }}
        >
          <FormattedMessage
            defaultMessage="+ Přidat proceduru"
            id="visitDetail.addProcedureButton"
          />
        </Button>
      )}
      <SubmitFormDialog
        methods={methods}
        ref={addProcedureDialogRef}
        onOpen={() =>
          methods.reset({
            description: defaultValues?.description ?? '',
            stockAllowances: mapDefaultStockAlowances(editableAllowances),
          })
        }
        onSubmit={formData => {
          if (createNewProcedure.isPending) {
            return false;
          }
          createNewProcedure.mutate({
            stockAllowances: [...formData.stockAllowances, ...readonlyAllowances],
            type: 'CONSUMPTION',
            description: formData.description,
            visitId,
            id: procedureId,
          } as unknown as PostNewProcedure);
          addProcedureDialogRef.current?.close();
          scroll();
        }}
        actions={
          <>
            {procedureId && (
              <Button
                onClick={() => deleteProcedure.mutate(procedureId)}
                endIcon={<DeleteOutlineIcon fontSize="small" />}
              >
                <FormattedMessage id="formDialog.delete" defaultMessage="Smazat" />
              </Button>
            )}
          </>
        }
        title={t('formDialogAddProcedure.addProcedure')}
        dialogHelperText={t('formDialogAddProcedure.helperText')}
      >
        <TextField
          fieldPath="description"
          label={t('formDialogAddProcedure.procedureDescription')}
          type="text"
          control={methods.control}
          fullWidth
          multiline
          minRows={2}
          maxRows={10}
        />
        <AddStockAllowanceForm
          control={methods.control}
          name="stockAllowances"
          remove={remove}
          fields={fields}
          append={append}
          readonlyAllowances={readonlyAllowances}
        />
      </SubmitFormDialog>
      {procedures &&
        procedures.map(procedure => (
          <ProcedureCard
            key={procedure.id}
            disabled={isVisitClosed}
            procedureId={procedure.id}
            orderNumber={procedure.stepOrder}
            type={procedure.type}
            description={procedure.description ?? ''}
            stockAllowances={procedure.stockAllowances}
          />
        ))}
    </>
  );
};

export default ProcedureFormDialog;
